import AppService from 'App/legacy/AppService';
export const OUTCOME_TYPES = {
	NO_ANSWER: 'not_reached',
	ANSWER: 'reached',
	POSTPONE: 'postpone'
} as const;

export const LEAD_TYPES = {
	NO_LEAD: 'no_lead_found',
	FOUND_LEAD: 'found_lead'
} as const;

export const FOLLOWUP_TYPES = {
	NEW_ACTIVITY: 'new_activity',
	BOOK_APPOINTMENT: 'book_appointment',
	CREATE_TODO: 'create_todo',
	PLAN_PHONE_CALL: 'plan_phone_call'
} as const;

export const NO_ANSWER_ACTIONS = {
	POSTPONE_DATE: 'postponed',
	SEND_MAIL: 'send_mail',
	STOP_TRYING: 'stop_trying',
	TALK_TO_SOMEONE: 'change_contact'
} as const;

export const ANSWER_ACTIONS = {
	BUSY: 'busy',
	STOP_TRYING: 'stop_trying',
	NO_THANKS: 'no_thanks',
	SEND_MAIL: 'send_mail',
	TALK_TO_SOMEONE: 'change_contact'
} as const;

export const ANSWER_ACTIONS_GREAT = {
	BOOK_APPOINTMENT: 'book_appointment',
	CREATE_OPPORTUNITY: 'create_opportunity',
	CREATE_ORDER: 'create_order',
	PLAN_PHONE_CALL: 'plan_phone_call'
} as const;

export const NO_LEAD_ACTIONS = {
	POSTPONE_DATE: 'postponed',
	STOP_TRYING: 'stop_trying'
} as const;

export const FOUND_LEAD_ACTIONS = {
	ADDED_CONTACT: 'added_contact'
} as const;

export const DEFAULT_PRESET = 'nextDay';

export const POSTPONE_PRESETS = [
	'currentDay',
	'nextDay',
	'next7days',
	'next14days',
	'next21days',
	'next28days',
	'next63days',
	'next91days'
] as const;

export type PostponePreset = (typeof POSTPONE_PRESETS)[number];

/**
 * The rep's postpone preference, per user per account. The key is shared with ui-3
 * (`ui-3/src/lib/activityWrite.ts`), so the shape has to stay compatible with it.
 *
 * `remember` is what makes the memory opt-in: without it a one-off 60-day postpone would
 * silently become the default for every following activity, which is what reps complained
 * about. Absent means off, so reps carrying a preset from before the toggle existed get the
 * plain default back until they tick the box.
 */
export type PostponePreference = {
	preset: string;
	timeOffsetMinutes?: number;
	remember?: boolean;
};

const preferenceStorageKey = (): string => {
	const self = AppService.getSelf();
	const customerId = AppService.getCustomerId();
	return `ls.activityOutcomePostponePreset-${customerId}-${self?.id || ''}`;
};

/** Null when nothing is stored, or when localStorage is unavailable or holds corrupted JSON. */
export const getPostponePreference = (): PostponePreference | null => {
	try {
		const stored = localStorage.getItem(preferenceStorageKey());
		return stored ? (JSON.parse(stored) as PostponePreference) : null;
	} catch (error) {
		console.warn('Could not read the stored postpone preference', error);
		return null;
	}
};

export const setPostponePreference = (preference: PostponePreference): void => {
	try {
		localStorage.setItem(preferenceStorageKey(), JSON.stringify(preference));
	} catch (error) {
		console.warn('Could not store the postpone preference', error);
	}
};

/** Whether the rep asked us to remember their postpone choice. Off unless they ticked it. */
export const isRememberPostponeEnabled = (): boolean => getPostponePreference()?.remember === true;

/**
 * Turning it off drops the remembered preset with it, so the next activity opens on the default
 * rather than sitting on a stale choice the rep can no longer see the reason for. `preset` is the
 * rep's current pick, captured on the way in so ticking the box does not make them choose it a
 * second time.
 */
export const setRememberPostponeEnabled = (remember: boolean, preset?: string | null): void => {
	if (!remember) {
		setPostponePreference({ preset: DEFAULT_PRESET, remember: false });
		return;
	}
	const current = getPostponePreference();
	setPostponePreference({ ...current, preset: preset ?? current?.preset ?? DEFAULT_PRESET, remember: true });
};

/**
 * The preset to preselect: the remembered one when the rep opted in and it is still offered
 * to them, otherwise DEFAULT_PRESET.
 */
export const getStoredDefaultPreset = (): string => {
	const preference = getPostponePreference();
	if (preference?.remember !== true) {
		return DEFAULT_PRESET;
	}
	const { preset } = preference;
	return preset && POSTPONE_PRESETS.includes(preset as PostponePreset) ? preset : DEFAULT_PRESET;
};

export const POSTPONED = 'postponed';
export const USER_CHANGED = 'userChanged';

export const CHANGED_CONTACT = 'changed_contact';

export const SET_DATE = 'set_date';

export const OUTCOME_BACKEND_ACTIONS = {
	[ANSWER_ACTIONS.BUSY]: POSTPONED
} as const;

export const APPOINTMENT_ACTIONS = {
	CANCELLED: 'cancelled'
} as const;

export const APPOINTMENT_OUTCOME_TYPES = {
	PLANNED: 'planned',
	COMPLETED: 'completed',
	NOT_COMPLETED: 'notCompleted'
} as const;
